/**
 * Demo storyboard — the single, DRY source of truth for every step.
 *
 * Each step optionally navigates to a workspace ``tab`` (so the section's real
 * content is visible behind the spotlight), anchors to a stable ``target``
 * (a ``data-tour="…"`` attribute — never a brittle class/role selector), and
 * carries Lingui ``msg`` copy. ``target: null`` renders a centered card
 * (welcome / finish). The renderer (``DemoTour.tsx``) is generic over this
 * list, so adding or reordering a step is a one-line edit here.
 */
import { msg } from "@lingui/core/macro";

import type { MessageDescriptor } from "@lingui/core";
import type { WorkspaceTab } from "@/features/workspace-router";

export type TourPlacement = "center" | "top" | "bottom" | "left" | "right";

export interface DemoTourStep {
  readonly id: string;
  /** ``data-tour`` selector to spotlight, or ``null`` for a centered card. */
  readonly target: string | null;
  readonly placement: TourPlacement;
  /** Navigate to this workspace tab before showing the step (optional). */
  readonly tab?: WorkspaceTab;
  readonly title: MessageDescriptor;
  readonly body: MessageDescriptor;
  /** Copy variant for real signed-in users (``mode === "user"``); falls back
   *  to ``body`` when absent. Lets the same steps serve demo + first-run. */
  readonly bodyUser?: MessageDescriptor;
}

export const DEMO_TOUR_STEPS: readonly DemoTourStep[] = [
  {
    id: "welcome",
    target: null,
    placement: "center",
    title: msg`Benvenuto in Ando`,
    body: msg`Questo è il Company Brain di Rossi Forniture, un'azienda demo con email, gestionale, banca, documenti e calendario già collegati. Ti accompagno in un giro veloce — meno di un minuto.`,
    bodyUser: msg`Ando è il tuo Company Brain: unisce email, documenti, gestionale e calendario in un'unica mente che ricorda, capisce e agisce. Ti mostro dove trovi cosa, in meno di un minuto. Puoi saltare quando vuoi.`,
  },
  {
    id: "composer",
    target: '[data-tour="composer"]',
    placement: "top",
    title: msg`Chiedi in linguaggio naturale`,
    body: msg`Da qui parli con Ando come a un collega. "Focus" sceglie se cercare nei documenti, nei dati strutturati o in entrambi; "Strumenti" e "Ambito" affinano la ricerca. Prova: «Quali fatture sono scadute?»`,
  },
  {
    id: "focus",
    target: '[data-tour="focus"]',
    placement: "top",
    title: msg`Interroga i dati strutturati`,
    body: msg`Per chiedere di gestionale, banca o fatturato apri «Ambito» e seleziona la fonte dati — ad esempio il connettore Gestionale. Così Ando sa dove cercare. Prova: «Quali fatture sono scadute?» oppure «5 clienti per fatturato».`,
    bodyUser: msg`Per interrogare i tuoi dati strutturati (gestionale, banca, fatturato) apri «Ambito» e seleziona la fonte dati, ad esempio il tuo connettore Gestionale. Così Ando sa dove cercare. Prova: «Quali fatture sono scadute?».`,
  },
  {
    id: "tabs",
    target: '[data-tour="workspace-tabs"]',
    placement: "bottom",
    title: msg`Le tue aree di lavoro`,
    body: msg`Qui sopra ci sono le aree principali: conversazioni, calendario, documenti, amministrazione, vendite e altro. Le aree più avanzate — Canvas, Analisi, Automazioni, Flows, Connect — vivono negli «Spazi», nella barra a sinistra: te le mostro tra poco.`,
  },
  {
    id: "conversations",
    target: '[data-tour="tab-conversations"]',
    placement: "bottom",
    tab: "conversations",
    title: msg`Conversazioni`,
    body: msg`Email, PEC e chat di tutti gli account in un'unica casella. Chiedi ad Ando di riassumere o di scrivere una risposta — gli invii in demo sono simulati, niente parte davvero.`,
    bodyUser: msg`Email, PEC e chat di tutti i tuoi account in un'unica casella. Chiedi ad Ando di riassumere, dare priorità o scrivere una risposta.`,
  },
  {
    id: "calendar",
    target: '[data-tour="tab-calendar"]',
    placement: "bottom",
    tab: "calendar",
    title: msg`Calendario`,
    body: msg`Riunioni e impegni della settimana. Ando può prepararti un brief per il prossimo meeting o trovarti uno slot libero.`,
  },
  {
    id: "documents",
    target: '[data-tour="tab-documents"]',
    placement: "bottom",
    tab: "documents",
    title: msg`Documenti`,
    body: msg`La conoscenza aziendale — HR, policy, manuali tecnici, troubleshooting — indicizzata e interrogabile. Chiedi «Qual è la policy ferie?» e Ando risponde citando il documento.`,
  },
  {
    id: "finance",
    target: '[data-tour="tab-finance"]',
    placement: "bottom",
    tab: "finance",
    title: msg`Amministrazione e dati strutturati`,
    body: msg`Gestionale, banca e fatturazione. Qui Ando interroga i dati strutturati: scadenziario, cash flow, clienti per fatturato. Prova «Mostrami i 5 clienti con più fatturato».`,
  },
  {
    id: "work",
    target: '[data-tour="tab-work"]',
    placement: "bottom",
    tab: "work",
    title: msg`Azioni e attività`,
    body: msg`Ando non solo risponde: propone azioni (inviare un'email, emettere una fattura, creare un evento). Ogni azione è una proposta che vedi, modifichi e approvi — in demo l'esecuzione è simulata.`,
  },
  {
    id: "spazi",
    target: '[data-tour="spazi"]',
    placement: "right",
    title: msg`Gli Spazi`,
    body: msg`Nella barra a sinistra trovi gli «Spazi», le aree più avanzate: Canvas (cervelli verticali per cliente o progetto), Analisi, Automazioni, Flows e Connect. Sono sempre a un clic, senza affollare la barra in alto.`,
    bodyUser: msg`Nella barra a sinistra trovi gli «Spazi»: Canvas (cervelli verticali per cliente o progetto), Analisi, Automazioni, Flows e Connect. Sono le aree più avanzate, sempre a portata di clic.`,
  },
  {
    id: "finish",
    target: null,
    placement: "center",
    title: msg`Tutto qui. Tocca a te.`,
    body: msg`Torna in chat e fai la tua prima domanda. Un buon inizio: «Riassumi le email che richiedono risposta» oppure «Qual è la policy ferie aziendale?». Puoi rivedere questo giro dalla schermata iniziale.`,
    bodyUser: msg`Torna in chat e fai la tua prima domanda, oppure collega un nuovo strumento dai Connettori. Puoi rivedere questo giro quando vuoi dalla schermata iniziale.`,
  },
] as const;
